import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/apiService';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { theme as themeTokens, rgba, previewThemeToken, recomputeShellBlend, SHELL_GRADIENT_KEYS } from '../theme';

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
const ROLE_RANK = { viewer: 0, analyst: 1, employee: 1, manager: 2, admin: 3, superadmin: 4 };

const TOKEN_GROUPS = [
  {
    title: 'Brand Colors',
    description: 'Used for buttons, links, highlights and accents across the platform.',
    fields: [
      { key: 'primaryColor', label: 'Primary' },
      { key: 'secondaryColor', label: 'Secondary' },
      { key: 'accentColor', label: 'Accent' },
    ],
  },
  {
    title: 'KPI Tiles',
    description: 'Background and border of dashboard metric cards, charts and quick-action panels.',
    fields: [
      { key: 'tileBackground', label: 'Tile Background' },
      { key: 'tileBorder', label: 'Tile Border' },
    ],
  },
  {
    title: 'Sidebar',
    description: 'Left navigation panel background and inactive item text.',
    fields: [
      { key: 'sidebarBackground', label: 'Sidebar Background' },
      { key: 'sidebarText', label: 'Sidebar Text' },
    ],
  },
  {
    title: 'Overall Background',
    description: 'The base backdrop behind the sidebar and every page. Gradient Accent defaults to your Accent Color above if left blank.',
    fields: [
      { key: 'shellBackground', label: 'Background' },
      { key: 'shellBackgroundAccent', label: 'Gradient Accent', placeholder: 'Same as Accent Color' },
    ],
  },
];
const ALL_KEYS = TOKEN_GROUPS.flatMap(g => g.fields.map(f => f.key));

function ColorField({ label, value, onChange, invalid, placeholder = '#000000' }) {
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      <div style={styles.swatchRow}>
        <input
          type="color"
          value={HEX_COLOR.test(value) ? value : '#000000'}
          onChange={e => onChange(e.target.value)}
          style={styles.colorInput}
        />
        <input
          type="text"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          style={{ ...styles.hexInput, border: invalid ? '1px solid #ef4444' : styles.hexInput.border }}
        />
      </div>
    </div>
  );
}

function Message({ message }) {
  if (!message) return null;
  return (
    <div style={{
      marginBottom: 16, padding: '10px 16px', borderRadius: 8, fontSize: 13,
      background: message.isError ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
      color: message.isError ? '#ef4444' : '#22c55e',
      border: `1px solid ${message.isError ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`,
    }}>
      {message.text}
    </div>
  );
}

// ── Company Branding (admin-only): edits Tenant.branding directly ──────────
function CompanyBrandingSection({ onSaved }) {
  const [values, setValues] = useState(null);
  const [savedValues, setSavedValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getBranding();
      setValues(data.branding || {});
      setSavedValues(data.branding || {});
    } catch (err) {
      setMessage({ text: err.message || 'Failed to load branding.', isError: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (text, isError = false) => {
    setMessage({ text, isError });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleChange = (key, hex) => {
    setValues(prev => {
      const next = { ...prev, [key]: hex };
      previewThemeToken(next, key, hex);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const key of ALL_KEYS) {
        if (values[key] && HEX_COLOR.test(values[key])) payload[key] = values[key];
      }
      const data = await apiService.updateBranding(payload);
      setValues(data.branding || {});
      setSavedValues(data.branding || {});
      await onSaved?.();
      flash('Company branding saved.');
    } catch (err) {
      flash(err.message || 'Failed to save branding.', true);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setValues(savedValues || {});
    for (const key of ALL_KEYS) {
      if (savedValues?.[key]) previewThemeToken(savedValues, key, savedValues[key]);
    }
  };

  if (loading || !values) {
    return <p style={{ color: '#94a3b8' }}>Loading company branding...</p>;
  }

  return (
    <div style={{ marginBottom: '40px' }}>
      <div style={styles.sectionHeader}>
        <div>
          <h2 style={styles.sectionTitle}>Company Branding</h2>
          <p style={styles.sectionDesc}>Sets the default colors every user in your company sees, unless they set a personal override below.</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={styles.secondaryBtn} onClick={handleReset} disabled={saving}>Reset</button>
          <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>

      <Message message={message} />

      {TOKEN_GROUPS.map(group => (
        <div key={group.title} style={styles.group}>
          <h3 style={styles.groupTitle}>{group.title}</h3>
          <p style={styles.groupDesc}>{group.description}</p>
          <div style={styles.fieldRow}>
            {group.fields.map(({ key, label, placeholder }) => (
              <ColorField
                key={key}
                label={label}
                value={values[key]}
                invalid={values[key] && !HEX_COLOR.test(values[key])}
                onChange={hex => handleChange(key, hex)}
                placeholder={placeholder}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── My Theme (everyone): personal overrides on Dashboard.theme ─────────────
function MyThemeSection({ onSaved }) {
  const [tenantDefaults, setTenantDefaults] = useState(null);
  const [originalOverrides, setOriginalOverrides] = useState(null);
  const [draftOverrides, setDraftOverrides] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getMyTheme();
      setTenantDefaults(data.tenant || {});
      setOriginalOverrides(data.user || {});
      setDraftOverrides(data.user || {});
    } catch (err) {
      setMessage({ text: err.message || 'Failed to load your theme.', isError: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (text, isError = false) => {
    setMessage({ text, isError });
    setTimeout(() => setMessage(null), 4000);
  };

  const displayValue = key => draftOverrides?.[key] ?? tenantDefaults?.[key] ?? '';
  const hasAnyOverride = Object.keys(draftOverrides || {}).length > 0;

  // The full "what's actually shown" view for a given draft — this user's
  // override where set, else the company default — same precedence chain
  // as everywhere else. previewThemeToken needs this (not just the one
  // field that changed) to correctly recompute the shell gradient's blend,
  // which depends on up to three fields at once.
  const effectiveFor = draft => {
    const out = {};
    for (const key of ALL_KEYS) out[key] = draft[key] ?? tenantDefaults?.[key];
    return out;
  };

  const handleChange = (key, hex) => {
    setDraftOverrides(prev => {
      const next = { ...prev, [key]: hex };
      previewThemeToken(effectiveFor(next), key, hex);
      return next;
    });
  };

  const handleResetField = key => {
    setDraftOverrides(prev => {
      const next = { ...prev };
      delete next[key];
      const eff = effectiveFor(next);
      if (tenantDefaults?.[key]) previewThemeToken(eff, key, tenantDefaults[key]);
      else if (SHELL_GRADIENT_KEYS.includes(key)) recomputeShellBlend(eff);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const key of ALL_KEYS) {
        const isOverriddenNow = key in draftOverrides && HEX_COLOR.test(draftOverrides[key]);
        const wasOverriddenBefore = key in originalOverrides;
        if (isOverriddenNow) payload[key] = draftOverrides[key];
        else if (wasOverriddenBefore) payload[key] = null; // explicit reset to company default
      }
      const data = await apiService.updateMyTheme(payload);
      setTenantDefaults(data.tenant || {});
      setOriginalOverrides(data.user || {});
      setDraftOverrides(data.user || {});
      await onSaved?.();
      flash('Your theme saved.');
    } catch (err) {
      flash(err.message || 'Failed to save your theme.', true);
    } finally {
      setSaving(false);
    }
  };

  const handleResetAll = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const key of ALL_KEYS) payload[key] = null;
      const data = await apiService.updateMyTheme(payload);
      setTenantDefaults(data.tenant || {});
      setOriginalOverrides(data.user || {});
      setDraftOverrides(data.user || {});
      for (const key of ALL_KEYS) {
        if (data.tenant?.[key]) previewThemeToken(data.tenant, key, data.tenant[key]);
      }
      recomputeShellBlend(data.tenant || {});
      await onSaved?.();
      flash('All personal overrides cleared — now using your company theme.');
    } catch (err) {
      flash(err.message || 'Failed to reset your theme.', true);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !tenantDefaults) {
    return <p style={{ color: '#94a3b8' }}>Loading your theme...</p>;
  }

  return (
    <div>
      <div style={styles.sectionHeader}>
        <div>
          <h2 style={styles.sectionTitle}>My Theme</h2>
          <p style={styles.sectionDesc}>Optional personal colors — only you see these. Anything you don't override uses your company's branding.</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            style={{ ...styles.secondaryBtn, opacity: hasAnyOverride ? 1 : 0.5, cursor: hasAnyOverride ? 'pointer' : 'not-allowed' }}
            onClick={handleResetAll}
            disabled={saving || !hasAnyOverride}
            title={hasAnyOverride ? undefined : 'No personal overrides set yet'}
          >
            Reset All to Company Theme
          </button>
          <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save My Theme'}</button>
        </div>
      </div>

      <Message message={message} />

      {TOKEN_GROUPS.map(group => (
        <div key={group.title} style={styles.group}>
          <h3 style={styles.groupTitle}>{group.title}</h3>
          <div style={styles.fieldRow}>
            {group.fields.map(({ key, label, placeholder }) => {
              const overridden = key in draftOverrides;
              const value = displayValue(key);
              return (
                <div key={key}>
                  <ColorField
                    label={label}
                    value={value}
                    invalid={value && !HEX_COLOR.test(value)}
                    onChange={hex => handleChange(key, hex)}
                    placeholder={placeholder}
                  />
                  {overridden ? (
                    <button style={styles.resetLink} onClick={() => handleResetField(key)}>
                      ↺ Reset to company default
                    </button>
                  ) : (
                    <span style={styles.defaultHint}>Using company default</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CustomizationPage() {
  const { user } = useAuth();
  const { refreshBranding } = useTheme() || {};
  const isAdmin = (ROLE_RANK[user?.role] ?? -1) >= ROLE_RANK.admin;
  const [activeTab, setActiveTab] = useState(() => (isAdmin ? 'company' : 'personal'));

  // Editing either tab live-previews by mutating global CSS vars directly
  // (see previewThemeToken in theme.js). If you switch tabs — or
  // leave this page — with an edit still unsaved, that preview would
  // otherwise stay stuck applied everywhere (sidebar, dashboard, etc.)
  // forever. Re-fetching and re-applying the true saved theme on every tab
  // change and on unmount discards any such abandoned preview.
  useEffect(() => {
    return () => { refreshBranding?.(); };
  }, [activeTab, refreshBranding]);

  return (
    <div style={styles.page}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={styles.title}>Customization</h1>
        <p style={styles.sub}>Changes preview instantly — save to make them permanent.</p>
      </div>

      {isAdmin && (
        <div style={styles.tabBar}>
          <button
            style={activeTab === 'company' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('company')}
          >
            Company Branding
          </button>
          <button
            style={activeTab === 'personal' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('personal')}
          >
            My Theme
          </button>
        </div>
      )}

      {activeTab === 'company' && isAdmin
        ? <CompanyBrandingSection onSaved={refreshBranding} />
        : <MyThemeSection onSaved={refreshBranding} />}
    </div>
  );
}

const styles = {
  page:       { padding: '32px', minHeight: '100vh', background: `linear-gradient(135deg,${rgba(themeTokens.colors.primaryRgb, 0.05)},${rgba(themeTokens.colors.accentRgb, 0.05)})` },
  title:      { color: '#e2e8f0', fontSize: '28px', fontWeight: '700', margin: 0 },
  sub:        { color: themeTokens.colors.textOnPageBackground, fontSize: '14px', marginTop: '4px' },
  tabBar:     { display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid rgba(59,130,246,0.15)', paddingBottom: '0' },
  tab:        { padding: '10px 20px', background: 'transparent', border: 'none', borderBottom: '2px solid transparent', color: '#64748b', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  tabActive:  { padding: '10px 20px', background: 'transparent', border: 'none', borderBottom: '2px solid #3b82f6', color: '#e2e8f0', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', gap: '16px' },
  sectionTitle:  { color: '#e2e8f0', fontSize: '20px', fontWeight: '700', margin: 0 },
  sectionDesc:   { color: '#64748b', fontSize: '13px', marginTop: '4px', maxWidth: '480px' },
  saveBtn:    { padding: '12px 24px', background: 'linear-gradient(135deg,#6C63FF,#3b82f6)', border: 'none', borderRadius: '8px', color: 'white', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  secondaryBtn: { padding: '12px 24px', background: 'rgba(148,163,184,0.15)', border: '1px solid rgba(148,163,184,0.3)', borderRadius: '8px', color: '#94a3b8', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  group:      { background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(59,130,246,0.15)', borderRadius: '12px', padding: '20px 24px', marginBottom: '20px' },
  groupTitle: { color: '#e2e8f0', fontSize: '16px', fontWeight: '600', margin: 0 },
  groupDesc:  { color: '#64748b', fontSize: '13px', marginTop: '4px', marginBottom: '16px' },
  fieldRow:   { display: 'flex', flexWrap: 'wrap', gap: '20px' },
  field:      { minWidth: '180px' },
  label:      { display: 'block', color: '#94a3b8', fontSize: '12px', fontWeight: '600', marginBottom: '8px' },
  swatchRow:  { display: 'flex', alignItems: 'center', gap: '8px' },
  colorInput: { width: '36px', height: '36px', padding: 0, border: '1px solid rgba(59,130,246,0.3)', borderRadius: '6px', background: 'none', cursor: 'pointer' },
  hexInput:   { width: '100px', padding: '8px 10px', background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '6px', color: '#e2e8f0', fontSize: '13px', outline: 'none', fontFamily: 'monospace' },
  resetLink:  { marginTop: '6px', background: 'none', border: 'none', color: '#3b82f6', fontSize: '11px', cursor: 'pointer', padding: 0 },
  defaultHint: { display: 'block', marginTop: '6px', color: '#64748b', fontSize: '11px' },
};
